/**
 * v3.28.9 — Customer-facing OpSy chat widget (customer dashboard).
 *
 * Talks to POST /api/customer/opsy/chat (tenant+store+customer scoped).
 * Renders the propose → confirm flow: when OpSy returns a `proposal` with a
 * `confirm_token`, we show a one-tap "Confirm" button that echoes the token
 * back to execute. Subscription/date pickers render as choice chips. A
 * "Talk to a human" affordance hands off to the existing ticketing widget
 * (kept as fallback).
 *
 * Auth: customer session token from sessionStorage('customerSession').token.
 */
import React from 'react';
import CustomerChatText from './CustomerChatText';
import { Sparkles, Send, X, Loader2, Check, MessageCircle, LifeBuoy } from 'lucide-react';
import { AddressAutocomplete } from '../ui/address-autocomplete';
import useOpsyName from '../../hooks/useOpsyName';
import CustomerHandoffChat from './CustomerHandoffChat';
import { prepareOpsyChatRequest } from '../../utils/opsyChatRequest';

const getBackendUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => {
  try { return JSON.parse(sessionStorage.getItem('customerSession') || '{}').token || ''; }
  catch { return ''; }
};

const OpsyCustomerChat = ({ customerName, customerEmail, subdomain, storeId, onActionExecuted }) => {
  // storeId is the route storeSlug — per-store assistant name (v4.9.1)
  const opsyName = useOpsyName(storeId);
  const [open, setOpen] = React.useState(false);
  const [messages, setMessages] = React.useState([]); // {role, text, choices?, proposal?, executed?}
  const [input, setInput] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [sessionId, setSessionId] = React.useState(null);
  const [historyLoaded, setHistoryLoaded] = React.useState(false);
  const [handoffId, setHandoffId] = React.useState(null);
  const scrollRef = React.useRef(null);
  const pendingChatRequest = React.useRef(null);

  // #4 — Load this customer's recent chat history when the panel first opens
  // so they can see previous conversations/changes.
  React.useEffect(() => {
    if (!open || historyLoaded) return;
    (async () => {
      let prior = [];
      try {
        const token = getToken();
        const r = await fetch(`${getBackendUrl()}/api/customer/opsy/history?session_token=${encodeURIComponent(token)}`);
        if (r.ok) {
          const d = await r.json();
          prior = (d.messages || []).map((m) => ({
            role: m.role === 'user' ? 'user' : 'assistant',
            text: m.content || '',
            executed: m.executed ? {} : null,
          }));
          if (d.session_id) setSessionId(d.session_id);
          if (d.support_ticket_id) setHandoffId(current => current || d.support_ticket_id);
        }
      } catch { /* graceful */ }
      const greeting = {
        role: 'assistant',
        text: `Hi${customerName ? ` ${customerName.split(' ')[0]}` : ''}! I'm ${opsyName}. I can skip or pause deliveries, change your delivery days or address, swap or add meal items, connect you with a human, or answer questions about your plan. What can I help with?`,
      };
      // v4.12 — NON-CLOBBERING: if the customer clicked "Talk to a human"
      // (or sent a message) while this async history fetch was in flight,
      // their bubbles are already in state — keep them instead of replacing
      // with history/greeting (the race that made the handoff confirmation
      // silently vanish while the admin still got the ticket).
      setMessages((cur) => (cur.length ? cur : (prior.length ? prior : [greeting])));
      setHistoryLoaded(true);
    })();
  }, [open, historyLoaded, customerName, opsyName]);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const post = async (body) => {
    const token = getToken();
    const attempt = prepareOpsyChatRequest(pendingChatRequest.current, body);
    pendingChatRequest.current = attempt;
    const r = await fetch(`${getBackendUrl()}/api/customer/opsy/chat?session_token=${encodeURIComponent(token)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(attempt.body),
    });
    const data = await r.json();
    // A delivered application refusal still contains the customer's next step.
    // Only transport failures should retain the request for an uncertain retry.
    if (!r.ok) throw new Error('Unable to confirm the chat response');
    if (pendingChatRequest.current === attempt) pendingChatRequest.current = null;
    return data;
  };

  const pushAssistant = (data) => {
    const ticket = data.support_ticket_id || (data.executed?.kind === 'connect_human' && data.executed.ticket_id);
    if (ticket) setHandoffId(ticket);
    setMessages((m) => [...m, {
      role: 'assistant',
      text: data.reply || '…',
      choices: data.choices && (data.choices.options || []).length ? data.choices : null,
      proposal: data.proposal && data.proposal.confirm_token ? data.proposal : null,
      addressWidget: data.address_widget || null,
      executed: data.executed || null,
    }]);
    if (data.session_id) setSessionId(data.session_id);
    // #1 — Live refresh: when an action actually executed, refresh the
    // dashboard so the change reflects immediately (no manual reload).
    if (data.executed && typeof onActionExecuted === 'function') {
      try { onActionExecuted(); } catch { /* noop */ }
    }
  };

  const send = async (text, pickedChoice = null) => {
    // Special action: "Top up wallet" CTA → switch the dashboard to the
    // Wallet tab instead of sending a chat message.
    if (pickedChoice?.value === '__open_wallet__') {
      try { window.dispatchEvent(new CustomEvent('opsy:open-wallet')); } catch { /* noop */ }
      setMessages((m) => [...m, { role: 'assistant', text: 'Opening your Wallet. Your change is saved — after topping up, return here and say “continue my change”.' }]);
      return;
    }
    const content = (text ?? input).trim();
    if ((!content && !pickedChoice) || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: content || pickedChoice?.label }]);
    setSending(true);
    try {
      const body = { message: content, session_id: sessionId };
      if (pickedChoice) body.picked_choice = pickedChoice;
      const data = await post(body);
      pushAssistant(data);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', text: "Sorry — I couldn't reach the server. Please try again.", error: true }]);
    } finally {
      setSending(false);
    }
  };

  const confirm = async (proposal) => {
    if (sending) return;
    setMessages((m) => [...m, { role: 'user', text: 'Confirm' }]);
    setSending(true);
    try {
      const data = await post({ message: 'confirm', session_id: sessionId, confirm_token: proposal.confirm_token });
      pushAssistant(data);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', text: "Sorry — couldn't confirm. Please try again.", error: true }]);
    } finally {
      setSending(false);
    }
  };

  const handleTalkToHuman = async () => {
    if (sending) return;
    setMessages((m) => [...m, { role: 'user', text: 'I\'d like to talk to a human' }]);
    setSending(true);
    try {
      // Backend resolves the tenant/subdomain from the session (bug #2 fix —
      // no more frontend subdomain guessing).
      const token = getToken();
      const recent = messages.slice(-6).map((x) => `${x.role}: ${x.text}`).join('\n');
      const res = await fetch(`${getBackendUrl()}/api/customer/opsy/talk-to-human?session_token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // v4.12 — thread session_id so the handoff persists into THIS chat
        // (the endpoint now writes both turns to customer_opsy_messages).
        body: JSON.stringify({ recent_context: recent, session_id: sessionId }),
      });
      const data = await res.json().catch(() => ({}));
      // Adopt the server-resolved session id (minted when we had none) so
      // subsequent chats thread into the same conversation.
      if (data.session_id) setSessionId(data.session_id);
      if (res.ok && data.ok) {
        if (data.ticket_id) setHandoffId(data.ticket_id);
        setMessages((m) => [...m, { role: 'assistant', text: data.message || 'Your support request is saved for our team.' }]);
      } else {
        setMessages((m) => [...m, { role: 'assistant', text: "I couldn't reach our team just now. Please try again in a moment.", error: true }]);
      }
    } catch {
      setMessages((m) => [...m, { role: 'assistant', text: "I couldn't reach our team just now. Please try again in a moment.", error: true }]);
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid="opsy-customer-chat-launcher"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 shadow-lg transition-colors"
      >
        <Sparkles className="h-5 w-5" strokeWidth={2} />
        <span className="font-semibold text-sm">Ask {opsyName}</span>
      </button>
    );
  }

  if (handoffId) return (
    <div data-testid="opsy-customer-chat-panel" className="fixed bottom-20 right-2 left-2 sm:left-auto md:bottom-6 md:right-6 z-50 flex flex-col w-auto sm:w-[400px] h-[70vh] max-h-[600px] rounded-2xl bg-white shadow-2xl border border-zinc-200 overflow-hidden">
      <button type="button" aria-label="Close support chat" onClick={() => setOpen(false)} className="self-end p-3"><X className="h-5 w-5" /></button>
      <CustomerHandoffChat ticketId={handoffId} onClosed={() => { setHandoffId(null); setSessionId(null); setMessages([]); }} />
    </div>
  );

  return (
    <div
      data-testid="opsy-customer-chat-panel"
      className="fixed bottom-20 right-2 left-2 sm:left-auto md:bottom-6 md:right-6 z-50 flex flex-col w-auto sm:w-[400px] h-[70vh] max-h-[600px] rounded-2xl bg-white shadow-2xl border border-zinc-200 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-emerald-600 text-white">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" strokeWidth={2} />
          <span className="font-semibold">{opsyName}</span>
        </div>
        <button type="button" data-testid="opsy-customer-chat-close" onClick={() => setOpen(false)} className="opacity-90 hover:opacity-100">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-zinc-50">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-[14px] leading-snug ${
              m.role === 'user'
                ? 'bg-emerald-600 text-white'
                : m.error
                  ? 'bg-rose-50 text-rose-700 border border-rose-200'
                  : 'bg-white text-zinc-800 border border-zinc-200'
            }`}>
              <CustomerChatText text={m.text} />

              {/* Executed confirmation */}
              {m.executed && (
                <div className="mt-1.5 inline-flex items-center gap-1 text-[12px] text-emerald-700">
                  <Check className="h-3.5 w-3.5" strokeWidth={2.2} /> Done
                </div>
              )}

              {/* Choice chips (subscription / date pickers) */}
              {m.choices && i === messages.length - 1 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(m.choices.options || []).map((opt, oi) => (
                    <button
                      key={oi}
                      type="button"
                      data-testid={`opsy-customer-choice-${oi}`}
                      disabled={sending}
                      onClick={() => send(opt.label, { kind: opt.kind || m.choices.kind, label: opt.label, value: opt.value })}
                      className="rounded-lg border border-zinc-300 px-2.5 py-1 text-[12px] font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                    >
                      {oi + 1}. {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Address search widget (autocomplete + geocoding). Rendered
                  when OpSy asks for a new address so the customer picks a
                  full, geocoded address instead of typing free text. */}
              {m.addressWidget && i === messages.length - 1 && (
                <div className="mt-2" data-testid="opsy-customer-address-widget">
                  <AddressAutocomplete
                    placeholder="Search your new address…"
                    disabled={sending}
                    onSelect={(addr) => {
                      const full = addr.address || addr.display_name || '';
                      if (!full) return;
                      send(full, {
                        kind: 'address',
                        label: full,
                        value: full,
                        latitude: addr.latitude,
                        longitude: addr.longitude,
                        place_id: addr.place_id,
                        city: addr.city,
                        province: addr.state,
                        postal_code: addr.postal_code,
                      });
                    }}
                  />
                  {m.addressWidget.needs_date && (
                    <p className="mt-1 text-[11px] text-zinc-500">
                      Pick your address above — I&apos;ll confirm the delivery date next.
                    </p>
                  )}
                </div>
              )}

              {/* Propose-first confirm button */}
              {m.proposal && i === messages.length - 1 && (
                <div className="mt-2">
                  <button
                    type="button"
                    data-testid="opsy-customer-confirm"
                    disabled={sending}
                    onClick={() => confirm(m.proposal)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-[13px] font-semibold disabled:opacity-50"
                  >
                    {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" strokeWidth={2.2} />}
                    Confirm
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-2xl bg-white border border-zinc-200 px-3 py-2">
              <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-zinc-100 p-2.5 bg-white">
        <div className="flex items-end gap-2">
          <textarea
            data-testid="opsy-customer-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Ask ${opsyName} or describe a change…`}
            rows={1}
            className="flex-1 resize-none rounded-xl border border-zinc-300 px-3 py-2 text-[14px] outline-none focus:ring-1 focus:ring-emerald-400 max-h-24"
          />
          <button
            type="button"
            data-testid="opsy-customer-send"
            onClick={() => send()}
            disabled={sending || !input.trim()}
            className="shrink-0 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white p-2.5 disabled:opacity-50"
          >
            <Send className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
        {/* Human fallback — creates a support ticket (handled in /admin/support). */}
        <button
          type="button"
          data-testid="opsy-customer-talk-human"
          onClick={handleTalkToHuman}
          disabled={sending}
          className="mt-1.5 inline-flex items-center gap-1 text-[12px] text-zinc-400 hover:text-zinc-600 disabled:opacity-50"
        >
          <LifeBuoy className="h-3.5 w-3.5" /> Talk to a human instead
        </button>
      </div>
    </div>
  );
};

export default OpsyCustomerChat;
